<template>
  <div class="admin-view">
    <div class="view-header">
      <h2>Settings</h2>
    </div>

    <div class="settings-section">
      <h3>Account</h3>
      <form class="pw-form" @submit.prevent="changePassword">
        <label>
          Current password
          <input v-model="currentPassword" type="password" autocomplete="current-password" required />
        </label>
        <label>
          New password
          <input v-model="newPassword" type="password" autocomplete="new-password" required
                 :minlength="MIN_LEN" />
        </label>
        <label>
          Confirm new password
          <input v-model="confirmPassword" type="password" autocomplete="new-password" required />
        </label>
        <p class="hint">Minimum {{ MIN_LEN }} characters. Must differ from your current password.</p>

        <button type="submit" :disabled="saving">{{ saving ? 'Saving…' : 'Change password' }}</button>

        <p v-if="message" :class="['msg', ok ? 'ok' : 'err']">{{ message }}</p>
      </form>
    </div>

    <div class="settings-section">
      <h3>Site</h3>
      <p class="muted">Site-wide configuration options will appear here in a future update.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useAdminApi } from '@/composables/useAdminApi'

const { apiFetch } = useAdminApi()
const MIN_LEN = 12

const currentPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const saving = ref(false)
const message = ref('')
const ok = ref(false)

async function changePassword() {
  message.value = ''
  ok.value = false

  if (newPassword.value.length < MIN_LEN) {
    message.value = `New password must be at least ${MIN_LEN} characters.`
    return
  }
  if (newPassword.value !== confirmPassword.value) {
    message.value = 'New password and confirmation do not match.'
    return
  }
  if (newPassword.value === currentPassword.value) {
    message.value = 'New password must differ from the current one.'
    return
  }

  saving.value = true
  try {
    const res = await apiFetch('/admin/account/password', {
      method: 'PATCH',
      body: JSON.stringify({
        currentPassword: currentPassword.value,
        newPassword: newPassword.value,
      }),
    })
    const data = await res.json().catch(() => ({}))
    if (res.ok) {
      ok.value = true
      message.value = 'Password changed successfully.'
      currentPassword.value = ''
      newPassword.value = ''
      confirmPassword.value = ''
    } else {
      message.value = data.error || `Failed (${res.status}).`
    }
  } catch (e: any) {
    message.value = e?.message === 'Unauthorized' ? 'Session expired — please log in again.' : 'Something went wrong.'
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.settings-section {
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.25rem;
  color: var(--text-primary);
}
.settings-section h3 { margin: 0 0 0.75rem; color: var(--text-primary); }
.muted { color: var(--text-secondary); }

.pw-form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 380px;
}
.pw-form label {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  color: var(--text-secondary);
  font-size: 0.9rem;
}
.pw-form input {
  padding: 0.55rem 0.7rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg-input, var(--bg-surface));
  color: var(--text-primary);
  font-size: 0.95rem;
}
.pw-form input:focus {
  outline: none;
  border-color: var(--accent);
}
.hint { color: var(--text-secondary); font-size: 0.8rem; margin: 0; }
.pw-form button {
  align-self: flex-start;
  padding: 0.55rem 1.1rem;
  border: none;
  border-radius: 6px;
  background: var(--accent);
  color: var(--accent-contrast, #fff);
  font-weight: 600;
  cursor: pointer;
}
.pw-form button:disabled { opacity: 0.6; cursor: default; }
.msg { margin: 0.25rem 0 0; font-size: 0.9rem; }
.msg.ok { color: var(--success, #2e7d32); }
.msg.err { color: var(--danger, #c62828); }
</style>
